/**
 * Contenido legal de Nexum (Sprint 14 S14.2 — R1 GDPR).
 *
 * Los documentos viven aquí como markdown VERBATIM (no en i18n/es.ts, que se
 * ensuciaría con textos largos). Son contenido legal aprobado por el product
 * owner: NO parafrasear, NO resumir, NO "mejorar". Se montan tal cual.
 *
 * Placeholders deliberadamente literales (Yan los rellenará tras la revisión
 * legal): `[RESPONSABLE — nombre fiscal / CIF pendiente]` y `[FECHA]`. NO
 * inventar valores ni resolver la fecha.
 *
 * El render lo hace `components/legal/LegalMarkdown.tsx` (sin dependencia de
 * markdown). El patrón de enlace exige `[texto](url)`, así que los corchetes
 * sueltos de los placeholders se renderizan tal cual.
 */

export const PRIVACY_MARKDOWN = `# Política de Privacidad de Nexum

> **Documento en versión preliminar, pendiente de revisión legal.** Este texto describe de forma honesta cómo Nexum trata tus datos durante su fase de pruebas (beta). Será revisado por un profesional antes del lanzamiento público.

**Última actualización:** [FECHA]

## 1. Quién es el responsable de tus datos

El responsable del tratamiento de tus datos personales es **[RESPONSABLE — nombre fiscal / CIF pendiente]**, con domicilio en Calle Dr. Alfonso Chiscano Díaz, 10, 35019, Las Palmas de Gran Canaria, España.

Para cualquier cuestión relacionada con tus datos o con esta política, puedes escribirnos a **hola@nexumrun.com**.

## 2. Qué datos recogemos

Recogemos únicamente los datos necesarios para ofrecerte tu plan de entrenamiento y mejorarlo:

**Datos de tu cuenta**
- Tu dirección de correo electrónico.
- Tu nombre (si decides facilitarlo).
- Tu contraseña, que guardamos siempre cifrada (nunca en texto legible).
- Si te registras con Google o Apple, los identificadores que esos servicios nos proporcionan para autenticarte.

**Datos sobre tu entrenamiento y tu condición física (datos de salud)**
- Tu nivel, días de entrenamiento por semana, superficie habitual y si usas reloj o pulsómetro.
- Tu frecuencia cardiaca máxima, si la aportas.
- Los resultados de tus pruebas dentro de la app, como el test de VAM (velocidad aeróbica máxima).
- Los datos de las sesiones que completas.

Algunos de estos datos —los relativos a tu condición física y tu frecuencia cardiaca— son **datos de salud**, considerados una categoría especial por la normativa de protección de datos. Por eso los tratamos con especial cuidado y solo con tu consentimiento explícito.

**Datos de actividad física desde Strava (solo si conectas tu cuenta)**
- Si decides conectar Strava, accedemos a tus actividades para incorporarlas a tu seguimiento: distancia, duración, ritmos, frecuencia cardiaca, cadencia, desnivel y datos asociados a cada actividad.
- Esta conexión es voluntaria y puedes desconectarla cuando quieras.

## 3. Con qué base legal y para qué los usamos

- **Para crear y adaptar tu plan de entrenamiento.** Esta es la finalidad principal de Nexum. La base legal es la ejecución del servicio que has solicitado al registrarte y, para los datos de salud, tu **consentimiento explícito**.
- **Para mantener tu cuenta y autenticarte de forma segura.**
- **Para detectar y corregir errores técnicos**, mediante un servicio de monitorización configurado para no recoger tus datos personales (ver sección 5).

No utilizamos tus datos para publicidad, no los vendemos ni los cedemos a terceros con fines comerciales, y no los usamos para perfilarte con fines distintos de generar tu entrenamiento.

## 4. Dónde se guardan tus datos

Tus datos se almacenan en servidores ubicados en la **Unión Europea**:
- Base de datos en Neon (región de Fráncfort, Alemania).
- Aplicación alojada en Fly.io (región de París, Francia) y la interfaz web en Vercel.

## 5. Con quién los compartimos

Solo compartimos datos con los proveedores estrictamente necesarios para que el servicio funcione:

- **Strava** (solo si conectas tu cuenta): la sincronización con Strava implica una transferencia de datos desde sus servidores, ubicados en **Estados Unidos**. Esta transferencia solo ocurre si tú decides conectar Strava, y se rige además por la propia política de privacidad de Strava.
- **Sentry** (monitorización de errores): lo usamos para detectar fallos técnicos. Está **configurado para no capturar tus datos personales ni de salud** en los informes de error.
- **Proveedores de alojamiento** (Neon, Fly.io, Vercel): almacenan y sirven la aplicación, en la UE.

No hay ningún otro tercero. En particular, **no enviamos datos a plataformas publicitarias** como Meta, Google Ads u otras.

## 6. Cuánto tiempo los conservamos

Conservamos tus datos mientras tu cuenta esté activa. Si borras tu cuenta, eliminamos tus datos personales y de salud de nuestros sistemas (ver sección 8). Podemos conservar un registro mínimo y sin datos identificativos de que el borrado se produjo.

## 7. Cómo protegemos tus datos

- Tu contraseña se guarda cifrada.
- Los datos de acceso a servicios conectados como Strava se guardan cifrados.
- El acceso a la base de datos está restringido y protegido.
- Usamos conexiones seguras (HTTPS) en toda la aplicación.

Nexum está en fase de pruebas (beta). Aunque ponemos cuidado en proteger tus datos, ningún sistema es infalible, y te lo decimos con honestidad.

## 8. Tus derechos

Puedes ejercer en cualquier momento los siguientes derechos sobre tus datos:

- **Acceso:** saber qué datos tenemos sobre ti.
- **Rectificación:** corregir datos inexactos.
- **Supresión:** borrar tu cuenta y tus datos. Puedes hacerlo tú mismo desde la aplicación, o solicitándolo por correo.
- **Portabilidad:** obtener una copia de tus datos.
- **Oposición y limitación:** oponerte a determinados tratamientos o pedir que se limiten.
- **Retirar tu consentimiento** en cualquier momento, sin que ello afecte a la licitud del tratamiento previo.

Para ejercer cualquiera de estos derechos, escríbenos a **hola@nexumrun.com**. También tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos ([www.aepd.es](https://www.aepd.es)) si consideras que no hemos tratado tus datos correctamente.

## 9. Datos de menores

Nexum no está dirigido a menores de edad. No recogemos conscientemente datos de personas menores de 18 años. Si crees que un menor nos ha facilitado datos, escríbenos y los eliminaremos.

## 10. Cambios en esta política

Podemos actualizar esta política a medida que Nexum evoluciona. Si hacemos cambios significativos, te lo comunicaremos a través de la aplicación.

---

*Esta política refleja el funcionamiento real de Nexum en su fase beta. Para cualquier duda: hola@nexumrun.com*
`;

export const TERMS_MARKDOWN = `# Términos de Servicio de Nexum

> **Documento en versión preliminar, pendiente de revisión legal.** Este texto describe las condiciones de uso de Nexum durante su fase de pruebas (beta). Será revisado por un profesional antes del lanzamiento público.

**Última actualización:** [FECHA]

## 1. Qué es Nexum

Nexum es una aplicación que genera planes de entrenamiento de carrera a pie adaptados a tu nivel, tus objetivos y tu progreso. Al usar Nexum, aceptas estos términos.

El responsable del servicio es **[RESPONSABLE — nombre fiscal / CIF pendiente]**, con domicilio en Calle Dr. Alfonso Chiscano Díaz, 10, 35019, Las Palmas de Gran Canaria, España. Contacto: **hola@nexumrun.com**.

## 2. Aviso importante sobre tu salud

**Lee esto con atención antes de empezar a entrenar.**

Nexum te ofrece planes de entrenamiento deportivo, **no consejo médico**. Nexum no es un profesional sanitario y la información que te da no sustituye la valoración de un médico.

- **Consulta a tu médico antes de empezar cualquier plan de entrenamiento**, especialmente si tienes alguna condición de salud, problemas cardiacos, lesiones, has estado mucho tiempo sin hacer ejercicio, estás embarazada o tienes cualquier duda sobre tu capacidad para realizar actividad física intensa.
- **Entrenas bajo tu propia responsabilidad.** El esfuerzo físico conlleva riesgos. Tú decides qué sesiones realizas y con qué intensidad, escuchando a tu cuerpo.
- **Detente y busca atención médica** si durante el ejercicio sientes dolor en el pecho, mareo, dificultad para respirar, palpitaciones o cualquier síntoma que te preocupe.
- Los planes de Nexum se basan en los datos que tú aportas. Si esos datos son incorrectos, el plan puede no ser adecuado para ti.

Al usar Nexum, entiendes y aceptas que el entrenamiento es responsabilidad tuya y que Nexum no se hace responsable de lesiones o problemas de salud derivados de la práctica deportiva.

## 3. Nexum está en fase de pruebas (beta)

Nexum es software en desarrollo. Esto significa que:
- Puede contener errores, comportarse de forma inesperada o no estar disponible en algún momento.
- Las funciones pueden cambiar, añadirse o retirarse.
- Aunque cuidamos tus datos, te recomendamos no depender de Nexum como tu único registro de entrenamiento.

Te agradecemos que nos avises de cualquier problema que encuentres.

## 4. Tu cuenta

- Necesitas una cuenta para usar Nexum, y durante la beta, una invitación.
- Eres responsable de mantener la confidencialidad de tu contraseña y de la actividad de tu cuenta.
- Debes facilitar datos veraces. Los planes dependen de la exactitud de la información que aportas.
- Debes ser mayor de edad para usar Nexum.

## 5. Uso aceptable

Te comprometes a usar Nexum de buena fe y a no:
- Intentar acceder a cuentas o datos de otras personas.
- Interferir con el funcionamiento del servicio o intentar vulnerar su seguridad.
- Usar la aplicación con fines ilícitos.

Podemos suspender el acceso a cuentas que incumplan estos términos.

## 6. Conexión con Strava

Si conectas tu cuenta de Strava, lo haces voluntariamente y autorizas a Nexum a acceder a tus actividades para incorporarlas a tu seguimiento. Puedes desconectar Strava cuando quieras. El uso de Strava se rige también por sus propios términos y política de privacidad.

## 7. Propiedad

Los planes que Nexum genera son para tu uso personal. La aplicación, su diseño y su funcionamiento son propiedad de Nexum. Tus datos siguen siendo tuyos: puedes acceder a ellos y borrarlos según se describe en la Política de Privacidad.

## 8. Limitación de responsabilidad

Dentro de los límites que permite la ley, y teniendo en cuenta que Nexum es un servicio gratuito en fase de pruebas:
- Nexum se ofrece "tal cual", sin garantías de que esté libre de errores o disponible de forma ininterrumpida.
- Nexum no se responsabiliza de daños derivados del uso de la aplicación, incluida la práctica deportiva (ver sección 2), salvo en los casos en que la ley no permita excluir dicha responsabilidad.

## 9. Cómo terminar

Puedes dejar de usar Nexum y borrar tu cuenta en cualquier momento desde la aplicación. Nosotros podemos suspender o terminar el servicio, avisándote con la antelación razonable que sea posible durante la beta.

## 10. Ley aplicable

Estos términos se rigen por la legislación española. Para cualquier controversia, las partes se someten a los juzgados y tribunales que correspondan conforme a la ley.

## 11. Cambios en estos términos

Podemos actualizar estos términos a medida que Nexum evoluciona. Si hacemos cambios significativos, te lo comunicaremos a través de la aplicación.

---

*Estos términos reflejan el funcionamiento real de Nexum en su fase beta. Para cualquier duda: hola@nexumrun.com*
`;
